import type { ThemeColors } from "@/lib/themes/theme.types"

export function editorThemeVars(theme: ThemeColors): React.CSSProperties {
  const ep = theme.editorPanels
  return {
    // ── Header (editor-header.tsx) ──
    "--ed-header-bg": theme.bgSurface,
    "--ed-header-border": ep.header.border,
    "--ed-header-back-bg": ep.header.backBg,
    "--ed-header-back-border": ep.header.backBorder,
    "--ed-header-back-text": ep.header.backText,
    "--ed-header-back-hover": ep.header.backHover,
    "--ed-header-compile-bg": ep.header.compileBg,
    "--ed-header-compile-hover": ep.header.compileHover,
    "--ed-header-compile-active": ep.header.compileActive,
    "--ed-header-path-text": ep.header.pathText,
    "--ed-header-title-text": ep.header.titleText,
    "--ed-header-avatar-bg": theme.bgSurface,
    "--ed-icon-filter": ep.icon.filter,
    "--ed-icon-filter-mono": theme.iconFilterMono,
    "--ed-user-header": theme.accentPrimary,

    // ── Ribbon bar (ribbon-menu.tsx, ribbon-primitives.tsx, view-menu.tsx) ──
    "--ed-ribbon-bg": ep.ribbon.bg,
    "--ed-ribbon-border": ep.ribbon.border,
    "--ed-ribbon-text": ep.ribbon.text,
    "--ed-ribbon-divider": ep.ribbon.divider,
    "--ed-ribbon-tab-hover": ep.ribbon.tabHover,
    "--ed-ribbon-tab-active-bg": ep.ribbon.tabActiveBg,
    "--ed-ribbon-tab-active-text": ep.ribbon.tabActiveText,
    "--ed-ribbon-field-border": ep.ribbon.fieldBorder,
    "--ed-ribbon-field-bg": ep.ribbon.fieldBg,
    "--ed-ribbon-field-hover": ep.ribbon.fieldHover,
    "--ed-ribbon-field-text": ep.ribbon.fieldText,
    "--ed-ribbon-icon-hover": ep.ribbon.iconHover,
    "--ed-ribbon-download-hover": theme.borderDefault,
    "--ed-ribbon-group-divider": ep.ribbon.groupDivider,
    "--ed-ribbon-group-active-bg": ep.ribbon.groupActiveBg,
    "--ed-ribbon-popup-bg": ep.ribbon.popupBg,
    "--ed-ribbon-group-bg": ep.ribbon.groupBg,

    // ── File explorer frame (FileExplorerFrame.tsx, dockable-layout.tsx) ──
    "--ed-explorer-canvas-bg": ep.explorer.canvasBg,
    "--ed-explorer-sidebar-bg": theme.bgSurface,
    "--ed-explorer-sidebar-border": ep.explorer.sidebarBorder,
    "--ed-explorer-panel-bg": theme.bgSurface,
    "--ed-explorer-panel-border": ep.explorer.panelBorder,
    "--ed-explorer-panel-shadow": ep.explorer.panelShadow,
    "--ed-explorer-icon-hover-bg": ep.explorer.iconHoverBg,
    "--ed-explorer-selected-bg": ep.explorer.selectedBg,
    "--ed-layers-icon-bg": ep.explorer.layersIconBg,
    "--ed-layers-icon-border": ep.explorer.layersIconBorder,
    "--ed-layout-canvas-bg": ep.explorer.layoutCanvasBg,
    "--ed-dock-btn-bg": theme.bgSurface,
    "--ed-dock-btn-border": ep.dock.btnBorder,

    // ── Code editor panel (dock/panels/code-editor-panel.tsx) ──
    "--ed-panel-bg": theme.bgSurface,
    "--ed-breadcrumb-bg": ep.codeEditorPanel.breadcrumbBg,
    "--ed-breadcrumb-border": ep.codeEditorPanel.breadcrumbBorder,
    "--ed-breadcrumb-text": ep.codeEditorPanel.breadcrumbText,
    "--ed-breadcrumb-divider": ep.codeEditorPanel.breadcrumbDivider,
    "--ed-breadcrumb-muted-text": ep.codeEditorPanel.breadcrumbMutedText,

    // ── Console panel (dock/panels/console-panel.tsx, console-logs.tsx) ──
    "--ed-console-bg": theme.bgSurface,
    "--ed-console-inner-bg": ep.console.innerBg,
    "--ed-console-border": theme.borderDefault,
    "--ed-console-tab-active-border": ep.console.tabActiveBorder,
    "--ed-console-tab-active-border-width": ep.console.tabActiveBorderWidth,
    "--ed-console-row-text": ep.console.rowText,
    "--ed-console-row-border": ep.console.rowBorder,
    "--ed-console-tab-active-text": ep.console.tabActiveText,
    "--ed-console-tab-text": ep.console.tabText,
    "--ed-console-tab-hover-text": ep.console.tabHoverText,
    "--ed-console-empty-text": ep.console.emptyText,
    "--ed-console-code-bg": ep.console.codeBg,
    "--ed-console-tab-active-bg": ep.console.tabActiveBg,
    "--ed-console-warn-row-bg": ep.console.warnRowBg,

    // ── Console / PDF / code-editor status colors (error / warn / success) ──
    "--ed-status-error-bg": ep.status.errorBg,
    "--ed-status-error-border": ep.status.errorBorder,
    "--ed-status-error-text": ep.status.errorText,
    "--ed-status-error-badge-bg": ep.status.errorBadgeBg,
    "--ed-status-error-badge-text": ep.status.errorBadgeText,
    "--ed-status-warn-bg": ep.status.warnBg,
    "--ed-status-warn-border": ep.status.warnBorder,
    "--ed-status-warn-text": ep.status.warnText,
    "--ed-status-warn-badge-bg": ep.status.warnBadgeBg,
    "--ed-status-warn-badge-text": ep.status.warnBadgeText,
    "--ed-status-success-bg": ep.status.successBg,
    "--ed-status-success-border": ep.status.successBorder,
    "--ed-status-success-text": ep.status.successText,
    "--ed-status-success-badge-bg": ep.status.successBadgeBg,
    "--ed-status-success-badge-text": ep.status.successBadgeText,
    "--ed-status-neutral-badge-bg": ep.status.neutralBadgeBg,
    "--ed-status-link-text": ep.status.linkText,
    "--ed-status-link-hover": ep.status.linkHover,
    "--ed-status-error-text-strong": ep.status.errorTextStrong,
    "--ed-status-error-dismiss-hover": ep.status.errorDismissHover,

    // ── PDF / EPUB preview panels (pdf-preview-panel, epub-preview, epub-preview-panel, synctex) ──
    "--ed-preview-canvas-bg": ep.preview.canvasBg,
    "--ed-preview-wrapper-bg": ep.preview.wrapperBg,
    "--ed-preview-bg-alt": ep.preview.bgAlt,
    "--ed-preview-tab-bar-bg": ep.preview.tabBarBg,
    "--ed-preview-tab-border": ep.preview.tabBorder,
    "--ed-preview-content-bg": ep.preview.contentBg,
    "--ed-preview-text": ep.preview.text,
    "--ed-preview-muted-text": ep.preview.mutedText,
    "--ed-preview-hint-icon": ep.preview.hintIcon,
    "--ed-preview-error-text": ep.preview.errorText,
    "--ed-preview-sub-text": theme.textSecondary,
    "--ed-preview-page-area-bg": ep.preview.pageAreaBg,
    "--ed-preview-img-bg": ep.preview.imgBg,
    "--ed-preview-img-border": ep.preview.imgBorder,
    "--ed-pdf-error-bg": ep.pdf.errorBg,
    "--ed-pdf-error-border": ep.pdf.errorBorder,
    "--ed-pdf-error-text": ep.pdf.errorText,

    // ── Auto Actions panel (auto-actions-panel.tsx) ──
    "--ed-toggle-on-bg": ep.toggle.onBg,
    "--ed-toggle-track-bg": ep.toggle.trackBg,
    "--ed-toggle-track-border": ep.toggle.trackBorder,
    "--ed-toggle-knob-bg": ep.toggle.knobBg,
    "--ed-toggle-knob-border-on": ep.toggle.knobBorderOn,
    "--ed-toggle-knob-border-off": ep.toggle.knobBorderOff,
    "--ed-toggle-label-on": ep.toggle.labelOn,
    "--ed-toggle-label-off": ep.toggle.labelOff,
    "--ed-dropdown-trigger-bg": ep.dropdown.triggerBg,
    "--ed-dropdown-trigger-border": ep.dropdown.triggerBorder,

    // ── Version history panel (version-history-panel.tsx) ──
    "--ed-vh-panel-bg": theme.bgSurface,
    "--ed-vh-header-border": ep.versionHistory.headerBorder,
    "--ed-vh-title-text": ep.versionHistory.titleText,
    "--ed-vh-close-text": ep.versionHistory.closeText,
    "--ed-vh-close-hover-bg": ep.versionHistory.closeHoverBg,
    "--ed-vh-subtext": ep.versionHistory.subtext,
    "--ed-vh-group-label-border": ep.versionHistory.groupLabelBorder,
    "--ed-vh-group-label-text": ep.versionHistory.groupLabelText,
    "--ed-vh-empty-text": ep.versionHistory.emptyText,
    "--ed-vh-item-hover-bg": ep.versionHistory.itemHoverBg,
    "--ed-vh-item-selected-border": ep.versionHistory.itemSelectedBorder,
    "--ed-vh-item-selected-bg": ep.versionHistory.itemSelectedBg,
    "--ed-vh-item-time-text": ep.versionHistory.itemTimeText,
    "--ed-vh-item-name-text": ep.versionHistory.itemNameText,
    "--ed-vh-item-desc-text": ep.versionHistory.itemDescText,
    "--ed-vh-item-hash-text": ep.versionHistory.itemHashText,
    "--ed-vh-icon-accent": ep.versionHistory.iconAccent,
    "--ed-vh-footer-border": ep.versionHistory.footerBorder,
    "--ed-vh-footer-bg": ep.versionHistory.footerBg,
    "--ed-vh-footer-label": ep.versionHistory.footerLabel,
    "--ed-vh-code-border": ep.versionHistory.codeBorder,
    "--ed-vh-code-bg": ep.versionHistory.codeBg,
    "--ed-vh-code-text": ep.versionHistory.codeText,
    "--ed-vh-offline-bg": ep.versionHistory.offlineBg,
    "--ed-vh-offline-border": ep.versionHistory.offlineBorder,
    "--ed-vh-offline-text": ep.versionHistory.offlineText,
    "--ed-vh-scrollbar-thumb": ep.versionHistory.scrollbarThumb,
    "--ed-vh-panel-shadow": ep.versionHistory.panelShadow,
    "--ed-vh-loading-border": ep.versionHistory.loadingBorder,

    // ── Cookie consent dialog (cookie-consent-dialog.tsx) ──
    "--ed-cookie-bg": theme.bgSurface,
    "--ed-cookie-border": ep.cookie.border,
    "--ed-cookie-title-text": ep.cookie.titleText,
    "--ed-cookie-body-text": ep.cookie.bodyText,
    "--ed-cookie-btn-border": ep.cookie.btnBorder,
    "--ed-cookie-btn-hover": ep.cookie.btnHover,

    // ── Tabs (detachable-tab.tsx, tabs/BrowserTab.tsx) ──
    "--ed-tab-active-bg": ep.tabs.activeBg,
    "--ed-tab-active-text": ep.tabs.activeText,
    "--ed-tab-inactive-text": ep.tabs.inactiveText,
    "--ed-tab-inactive-hover-text": ep.tabs.inactiveHoverText,
    "--ed-tab-inactive-hover-bg": ep.tabs.inactiveHoverBg,
    "--ed-tab-close-hover-bg": theme.borderDefault,
    "--ed-tab-bar-bg": ep.tabs.barBg,
    "--ed-tab-bar-border": ep.tabs.barBorder,
    "--ed-tab-detached-badge-bg": ep.tabs.detachedBadgeBg,
    "--ed-browser-tab-text": ep.browserTab.text,
    "--ed-browser-tab-btn-border": ep.browserTab.btnBorder,
    "--ed-browser-tab-btn-border-width": ep.browserTab.btnBorderWidth,
    "--ed-browser-tab-btn-bg-pdf": ep.browserTab.btnBgPdf,
    "--ed-browser-tab-btn-bg-file": ep.browserTab.btnBgFile,

    // ── Resize divider (divider-handle.tsx) ──
    "--ed-divider-bg": ep.divider.bg,
  } as React.CSSProperties
}
